import { useEffect, useRef, useState } from "react"
import { MOTION, useReducedMotion } from "../motion"

/**
 * A number that counts to its value instead of appearing at it.
 *
 * Only worth it because these tiles change while somebody is looking at them —
 * a socket delivers a message and "Unread messages" goes from 3 to 4. A digit
 * that simply swaps is a change you notice only if your eye happened to be
 * there; one that counts is a change you catch from the other side of the card.
 *
 * It animates from the previous value, not from zero, so that update travels one
 * step rather than replaying the whole tile. On first paint the previous value
 * IS zero, which is the one time the full count is the right thing to show.
 *
 * Driven by `requestAnimationFrame` against wall-clock time rather than a fixed
 * number of steps: a frame budget missed on a slow machine shortens the
 * animation instead of stretching it past its welcome.
 */
export default function CountUp({ value, duration = MOTION.slow }) {
  const target = Number(value) || 0
  const reduced = useReducedMotion()
  const [shown, setShown] = useState(target)
  const fromRef = useRef(target)
  const frameRef = useRef(null)

  useEffect(() => {
    const from = fromRef.current
    if (reduced || from === target) {
      fromRef.current = target
      setShown(target)
      return undefined
    }

    const started = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - started) / duration)
      // Ease out: the number arrives and settles rather than stopping dead.
      const eased = 1 - (1 - t) * (1 - t)
      setShown(Math.round(from + (target - from) * eased))
      if (t < 1) frameRef.current = requestAnimationFrame(step)
      else fromRef.current = target
    }

    frameRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frameRef.current)
  }, [target, duration, reduced])

  // Tabular figures, so a tile does not change width as it counts through 1 and 8.
  return <span style={{ fontVariantNumeric: "tabular-nums" }}>{shown}</span>
}
